import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '../../../../src/assets/svgImages/close-icon.svg';
import DownloadIcon from "../../../../src/assets/svgImages/Download-Icon.svg";
import './AddCategory.css';
import { fetchFileRequest, handleFetchFileSuccess } from '../../../../src/redux/actions/docCategoryAction';
import { useDispatch } from 'react-redux';

function FilePreview(props) {
    const [image, setImage] = useState("");
    const [zoomLevel, setZoomLevel] = useState(100);
    const dispatch = useDispatch();

    const handleZoomIn = () => {
        if (zoomLevel < 200) setZoomLevel(zoomLevel + 10);
    };

    const handleZoomOut = () => {
        if (zoomLevel > 50) setZoomLevel(zoomLevel - 10);
    };

    // Enhanced file type detection
    const getFileType = () => {
        return props.data.type || 
               props.data.mime_content_type || 
               props.data.mimeType || 
               props.data.mimetype || 
               '';
    };

    const fileType = getFileType();
    const isPDF = fileType === "application/pdf";

    useEffect(() => {
        const fetchAndPreviewFile = async () => {
            // console.log("=== PDF DEBUG ===");
            // console.log("File URL:", props.data.file);
            // console.log("File type:", getFileType());
            // console.log("Is PDF:", isPDF);
            
            if (isPDF) {
                try {
                    const response = await fetch(props.data.file);
                    if (response.ok) {
                        const blob = await response.blob();
                        
                        // blob has correct MIME type
                        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                        const blobUrl = URL.createObjectURL(pdfBlob);
                        

                        // console.log("Blob URL:", blobUrl);
                        
                        setImage(blobUrl);
                    } else {
                        throw new Error(`HTTP ${response.status}`);
                    }
                } catch (error) {
                    console.warn("Blob method failed, using Google Docs viewer:", error);
                    // Fallback: Use Google Docs viewer for better compatibility
                    const encodedUrl = encodeURIComponent(props.data.file);
                    const googleViewerUrl = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
                    setImage(googleViewerUrl);
                }
                dispatch(handleFetchFileSuccess(false));
            } else {
                // For non-PDFs: Use direct URL
                setImage(props.data.file);
                dispatch(handleFetchFileSuccess(false));
            }
        };
        
        if (props.data.file) {
            fetchAndPreviewFile();
        }

        // Cleanup blob URLs to prevent memory leaks
        return () => {
            if (image && image.startsWith('blob:')) {
                URL.revokeObjectURL(image);
            }
        };

    }, [props.data, isPDF]);

    return (
        <div className="modal-overlay">
            <div className="modal-content-container file-preview-model">
                <div className="modal-content" data-testid="file-preview-popup">
                    <div className="modal-title preview-header" data-testid="category-popup-title">
                        <div className='left-element' data-testid="file-title">{props.data.name}</div>
                        <div className='middle-element' style={isPDF ? { display: 'none' } : { display: 'flex' }}>
                            <div className="zoom-controls"
                                style={isPDF ? { display: 'none' } : { display: 'flex' }}>
                                <button title="Zoom Out" data-testid="zoom-out" onClick={handleZoomOut}> - </button>
                                <p title={zoomLevel} data-testid="zoom-level"> {zoomLevel}% </p>
                                <button title="Zoom In" data-testid="zoom-in" onClick={handleZoomIn}> + </button>
                            </div>
                        </div>
                        <div className='right-element'>
                            {/* Enhanced download - works for all file types with proper filename */}
                            <a href={props.data.file} 
                               download={props.data.name || "file"} 
                               target="_blank" 
                               rel="noreferrer">
                                <img className="download-icon" 
                                     data-testid="download-icon" 
                                     title="Download" 
                                     src={DownloadIcon} 
                                     alt=" " />
                            </a>
                            <button data-testid="close-icon" onClick={() => { props.onCancel() }}>
                                <img title="Close" src={CloseIcon} alt=" " />
                            </button>
                        </div>
                    </div>

                    <div className="image-preview">
                        <div style={{
                            transform: `scale(${zoomLevel / 100})`,
                            transformOrigin: zoomLevel < 100 ? "center" : "top left",
                            width: '100%',
                            height: '95%',
                            transition: 'transform 0.2s ease',
                        }}>
                            {isPDF ? (
                                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                    {/* Enhanced iframe with better Chrome compatibility */}
                                    <iframe
                                        src={image}
                                        width="100%"
                                        height="100%"
                                        style={{ border: 'none' }}
                                        title="PDF Preview"
                                        data-testid="pdf-preview-iframe"
                                        allow="fullscreen"
                                        type="application/pdf"
                                        // onLoad={() => console.log("PDF iframe loaded successfully")}
                                
                                    />
                                    {/* Enhanced fallback with multiple options */}
                                    <div style={{ 
                                        position: 'absolute', 
                                        bottom: '10px', 
                                        left: '10px', 
                                        fontSize: '12px', 
                                        color: '#666',
                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        zIndex: 1000
                                    }}>
                                        <div>If PDF doesn't display:</div>
                                        <a href={props.data.file} target="_blank" rel="noopener noreferrer" 
                                           style={{ color: '#007bff', textDecoration: 'underline' }}>
                                            Open in new tab
                                        </a>
                                        {' | '}
                                        <a href={props.data.file} download={props.data.name} 
                                           style={{ color: '#007bff', textDecoration: 'underline' }}>
                                            Download
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <img src={image} data-testid="image-preview" alt=" " />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

FilePreview.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        mime_content_type: PropTypes.string,
        mimeType: PropTypes.string,
        mimetype: PropTypes.string,
        file: PropTypes.string,
        name: PropTypes.string
    }),
    onCancel: PropTypes.func
};

export default FilePreview;